// ag-ui Overlay has a click-bubble bug: its onClick handler is attached to
// the root VerticalContainer without an e.target === e.currentTarget guard,
// so every bubbled click (including button clicks inside the modal body)
// would otherwise close the modal. The `handlePanelClick` stopPropagation
// below blocks the bubble. When the upstream ag-ui fix lands, remove the
// workaround. Tracked informally via the inline comment at line ~handlePanelClick;
// see commit RS-000 [svm-frontend] Modal primitive for the initial finding.
import { useEffect } from 'react';

import { Overlay, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import ModalHeader from './components/ModalHeader';
import { MODAL_PANEL_TEST_ID, TEST_ID } from './constants';
import type { FunctionComponent, ModalSize, Props } from './types';

import styles from './styles.module.scss';

const sizeClassMap: Record<ModalSize, string> = {
    default: styles.sizeDefault,
    small: styles.sizeSmall
};

const ESCAPE_KEY = 'Escape';
const BODY_OVERFLOW_LOCK = 'hidden';

const Modal: FunctionComponent<Props> = ({
    children,
    className = '',
    footer,
    isOpen,
    onClose,
    overlayClassName = '',
    size = 'default',
    testId = TEST_ID,
    title
}) => {
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === ESCAPE_KEY) {
                onClose();
            }
        };

        const previousBodyOverflow = document.body.style.overflow;

        document.body.style.overflow = BODY_OVERFLOW_LOCK;
        document.addEventListener('keydown', handleKeyDown);

        // TODO(v2): add focus trap via focus-trap-react or equivalent.
        // Deferred per Mixer's Q3 resolution — ship Modal v1 without focus trap,
        // accept the a11y gap until a real audit triggers the upgrade.

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    const sizeClass = sizeClassMap[size];

    // Workaround for ag-ui Overlay bug: its onClick handler is attached to
    // the root VerticalContainer without e.target === e.currentTarget guard,
    // so every bubbled click (including button clicks inside the modal body)
    // would otherwise close the modal. stopPropagation here blocks the bubble.
    // ag-ui typings the onClick prop as `VoidFunction` even though React wires
    // the DOM event through at runtime, so we cast once at the prop boundary.
    const handlePanelClick = ((event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
    }) as unknown as VoidFunction;

    return (
        <Overlay className={overlayClassName} onClose={onClose} testId={testId}>
            <VerticalContainer
                className={`${styles.panel} ${sizeClass} ${className}`}
                onClick={handlePanelClick}
                testId={MODAL_PANEL_TEST_ID}>
                <ModalHeader onClose={onClose} title={title} />

                <VerticalContainer className={styles.body}>{children}</VerticalContainer>

                {footer && <Wrapper className={styles.footer}>{footer}</Wrapper>}
            </VerticalContainer>
        </Overlay>
    );
};

export default Modal;
